import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthSession } from '@supabase/supabase-js';
import { selectPermissions, selectSession, selectUserStatus } from '../store/auth/auth.selectors';
import { ClinicPlanService } from '../services/clinic-plan.service';
import { planAllowsCode } from '../services/clinic-plan.rules';
import { resolveLandingRoute } from '../auth/password-gate';
import { map, take, filter, timeout, catchError } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';

/**
 * How long to wait for auth rehydration to resolve permissions before giving up
 * and sending the user to login. Measured once from navigation start.
 */
const PERMISSION_WAIT_MS = 5000;

export const permissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state) => {
  const store = inject(Store);
  const router = inject(Router);
  const plans = inject(ClinicPlanService);
  // A route may name ONE permission or SEVERAL, and several means "holding ANY ONE
  // admits". /opinions is the first route that needs it: its two tabs are now separate
  // permission modules, and a reader holding either has something to see there. Every
  // other route still passes a single string and reads exactly as before. Blank entries
  // are dropped so an empty string keeps its old meaning of "no permission required"
  // rather than becoming a code nobody can hold.
  const required = route.data['permission'] as string | string[] | undefined;
  const requiredPermissions = (Array.isArray(required) ? required : [required]).filter(
    (p): p is string => !!p,
  );

  return (
    combineLatest([
      store.select(selectPermissions),
      store.select(selectUserStatus),
      store.select(selectSession),
      plans.whenReady(),
    ]) as Observable<[string[] | null, string | null, AuthSession | null, unknown]>
  ).pipe(
    // `permissions === null` means "not loaded yet", so keep waiting. A missing
    // session means rehydration failed or the user was signed out, and no amount
    // of waiting will produce permissions — resolve immediately instead.
    filter(([permissions, , session]) => permissions !== null || !session),
    take(1),
    map(([permissions, status, session]): boolean | UrlTree => {
      if (!session) {
        return router.createUrlTree(['/login']);
      }

      // The clinic's plan comes first and binds owners too: a module the plan
      // leaves out is closed whatever the user's role. Landing on a closed
      // dashboard sends them to the first page their plan does open instead.
      const plan = plans.plan();
      const isOwner = plans.isOwner();
      const allows = (code: string) => planAllowsCode(plan, code, isOwner);
      if (requiredPermissions.length && !requiredPermissions.some(allows)) {
        if (state.url === '/dashboard' || state.url === '/') {
          const landing = resolveLandingRoute(permissions, status, allows);
          if (landing !== state.url && landing !== '/no-permission') return router.parseUrl(landing);
        }
        return router.createUrlTree(['/no-permission'], { queryParams: { reason: 'plan' } });
      }

      // If user has 'all' access status, allow
      if (status === 'all') {
        return true;
      }

      // If no specific permission required, allow
      if (!requiredPermissions.length) {
        return true;
      }

      // Check if the user holds ANY of the permissions this route names
      const hasAccess =
        !!permissions &&
        Array.isArray(permissions) &&
        requiredPermissions.some((p) => permissions.includes(p));

      if (!hasAccess) {
        console.warn(`User does not have permission: ${requiredPermissions.join(' or ')}. Permissions available: ${permissions}`);
        return router.createUrlTree(['/no-permission']);
      }

      return true;
    }),
    // Deliberately `first` and not the default `each`: a bare timeout(ms) is a
    // per-emission gap, which unrelated store emissions keep resetting. This is
    // one deadline from navigation start. `with` emits a route rather than
    // throwing — a guard that errors becomes a cancelled navigation that renders
    // nothing at all, which is the bug this replaces.
    timeout({
      first: PERMISSION_WAIT_MS,
      with: () => {
        console.error(
          `permissionGuard: permissions did not resolve within ${PERMISSION_WAIT_MS}ms; redirecting to login.`,
        );
        return of(router.createUrlTree(['/login']));
      },
    }),
    // Belt and braces: whatever else goes wrong, resolve to a route so the router
    // always has somewhere to land.
    catchError((err) => {
      console.error('permissionGuard failed unexpectedly; redirecting to login.', err);
      return of(router.createUrlTree(['/login']));
    }),
  );
};
